import { useEffect } from 'react';

// Declare fbq globally to avoid TypeScript errors
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export interface FacebookPixelEvent {
  event: string;
  parameters?: Record<string, any>;
}

/** Garantiza una sola inicialización por documento, aunque React remonte. */
let pixelInitialized = false;

export const isPixelInitialized = () => pixelInitialized;

export const useFacebookPixel = (pixelId: string = '1853428885267252') => {
  useEffect(() => {
    if (typeof window === 'undefined' || pixelInitialized) return;
    pixelInitialized = true;

    // Initialize Facebook Pixel if not already loaded
    if (!window.fbq) {
      // Facebook Pixel initialization code
      (function(f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', undefined, undefined, undefined);

      // Initialize the pixel
      window.fbq('init', pixelId);
      
      // Track initial PageView
      window.fbq('track', 'PageView');
    }
  }, [pixelId]);

  // Function to track custom events
  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.fbq) {
      if (parameters) {
        window.fbq('track', eventName, parameters);
      } else {
        window.fbq('track', eventName);
      }
    }
  };

  // Function to track page views
  const trackPageView = () => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
  };

  // Common e-commerce events
  const trackPurchase = (value: number, currency: string = 'MXN') => {
    trackEvent('Purchase', { value, currency });
  };

  const trackAddToCart = (contentName: string, value?: number) => {
    trackEvent('AddToCart', { content_name: contentName, value });
  };

  const trackLead = (contentName?: string) => {
    trackEvent('Lead', contentName ? { content_name: contentName } : undefined);
  };

  const trackCompleteRegistration = () => {
    trackEvent('CompleteRegistration');
  };

  const trackSubscribe = (value?: number) => {
    trackEvent('Subscribe', value ? { value, currency: 'MXN' } : undefined);
  };

  // Menu-specific events for restaurant analytics
  const trackMenuView = (menuName: string, restaurantName: string) => {
    trackEvent('ViewContent', {
      content_type: 'menu',
      content_name: menuName,
      restaurant_name: restaurantName
    });
  };

  const trackMenuItemView = (itemName: string, category: string, price?: number) => {
    trackEvent('ViewContent', {
      content_type: 'menu_item',
      content_name: itemName,
      content_category: category,
      value: price
    });
  };

  const trackQRScan = (restaurantName: string) => {
    trackEvent('Search', {
      search_string: 'qr_code_scan',
      restaurant_name: restaurantName
    });
  };

  return {
    trackEvent,
    trackPageView,
    trackPurchase,
    trackAddToCart,
    trackLead,
    trackCompleteRegistration,
    trackSubscribe,
    trackMenuView,
    trackMenuItemView,
    trackQRScan
  };
};